import { useMemo, useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { mockData, type SupportTicket } from '../api/client';
import { trpc } from '../api/trpc';

// Response templates for quick replies
const RESPONSE_TEMPLATES = [
  {
    id: 'greeting',
    name: 'Greeting',
    content: 'Hello! Thank you for reaching out to our support team. I\'m here to help you today.',
  },
  {
    id: 'more-info',
    name: 'Request More Info',
    content: 'Thank you for your message. To better assist you, could you please provide more details about:\n\n1. The specific issue you\'re experiencing\n2. Steps you\'ve already tried\n3. Any error messages you\'ve seen',
  },
  {
    id: 'escalation',
    name: 'Escalation Notice',
    content: 'I\'m escalating your ticket to our specialized team for further review. You\'ll hear back from them within 24 hours. In the meantime, please let me know if you have any other questions.',
  },
  {
    id: 'resolution',
    name: 'Issue Resolved',
    content: 'I\'m glad we could resolve this for you! If you have any other questions or concerns, please don\'t hesitate to reach out. Is there anything else I can help you with today?',
  },
  {
    id: 'billing',
    name: 'Billing Response',
    content: 'I understand you have a billing concern. I\'ve reviewed your account and can see the issue. Let me explain what happened and how we\'ll resolve it...',
  },
];

export default function Support() {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch tickets from API with mock fallback
  const { data: ticketsData } = useQuery({
    queryKey: ['support', 'tickets'],
    queryFn: async () => {
      const data = await trpc.support.listTickets.query({ limit: 50, offset: 0 });
      return { tickets: data.tickets as unknown as SupportTicket[] };
    },
    placeholderData: { tickets: mockData.supportTickets() },
  });

  const tickets: SupportTicket[] = (ticketsData?.tickets as SupportTicket[] | undefined) ?? mockData.supportTickets();
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [filter, setFilter] = useState<'all' | 'open' | 'in_progress' | 'resolved'>('all');
  const [replyText, setReplyText] = useState('');
  const [showTemplates, setShowTemplates] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [attachmentMessage, setAttachmentMessage] = useState<string | null>(null);

  // Mutations
  const updateStatus = useMutation({
    mutationFn: (input: { ticketId: string; status: 'open' | 'in_progress' | 'resolved' | 'closed' }) =>
      trpc.support.updateTicketStatus.mutate(input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['support', 'tickets'] }),
  });

  const replyToTicket = useMutation({
    mutationFn: (input: { ticketId: string; content: string }) =>
      trpc.support.replyToTicket.mutate(input),
    onSuccess: () => {
      setReplyText('');
      queryClient.invalidateQueries({ queryKey: ['support', 'tickets'] });
    },
  });

  const escalateTicket = useMutation({
    mutationFn: (ticketId: string) =>
      trpc.support.escalateTicket.mutate({ ticketId, priority: 'high' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['support', 'tickets'] }),
  });

  const filteredTickets = useMemo(() => {
    if (filter === 'all') return tickets;
    return tickets.filter((t: SupportTicket) => t.status === filter);
  }, [tickets, filter]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'bg-yellow-500/20 text-yellow-400';
      case 'in_progress':
        return 'bg-blue-500/20 text-blue-400';
      case 'resolved':
        return 'bg-green-500/20 text-green-400';
      default:
        return 'bg-slate-600 text-slate-300';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'bg-red-500/20 text-red-400';
      case 'high':
        return 'bg-orange-500/20 text-orange-400';
      case 'medium':
        return 'bg-yellow-500/20 text-yellow-400';
      case 'low':
        return 'bg-slate-600 text-slate-300';
      default:
        return 'bg-slate-600 text-slate-300';
    }
  };

  const getSenderColor = (sender: string) => {
    switch (sender) {
      case 'user':
        return 'bg-slate-700';
      case 'ai':
        return 'bg-purple-500/10 border border-purple-500/20';
      case 'agent':
        return 'bg-primary-500/10 border border-primary-500/20';
      default:
        return 'bg-slate-700';
    }
  };

  const getSenderLabel = (sender: string) => {
    switch (sender) {
      case 'user':
        return 'Customer';
      case 'ai':
        return 'AI Support';
      case 'agent':
        return 'Human Agent';
      default:
        return sender;
    }
  };

  const formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const statusCounts = useMemo(() => {
    return {
      open: tickets.filter((t) => t.status === 'open').length,
      in_progress: tickets.filter((t) => t.status === 'in_progress').length,
      resolved: tickets.filter((t) => t.status === 'resolved').length,
    };
  }, [tickets]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Support Tickets</h1>
        <p className="text-slate-400 mt-1">Manage escalated support tickets from AI support</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <button
          onClick={() => setFilter('all')}
          className={`card cursor-pointer transition-all text-left ${filter === 'all' ? 'ring-2 ring-primary-500' : 'hover:bg-slate-700/50'}`}
        >
          <p className="text-sm font-medium text-slate-400">All Tickets</p>
          <p className="text-3xl font-bold text-white mt-2">{tickets.length}</p>
        </button>
        <button
          onClick={() => setFilter('open')}
          className={`card cursor-pointer transition-all text-left ${filter === 'open' ? 'ring-2 ring-yellow-500' : 'hover:bg-slate-700/50'}`}
        >
          <p className="text-sm font-medium text-slate-400">Open</p>
          <p className="text-3xl font-bold text-yellow-400 mt-2">{statusCounts.open}</p>
        </button>
        <button
          onClick={() => setFilter('in_progress')}
          className={`card cursor-pointer transition-all text-left ${filter === 'in_progress' ? 'ring-2 ring-blue-500' : 'hover:bg-slate-700/50'}`}
        >
          <p className="text-sm font-medium text-slate-400">In Progress</p>
          <p className="text-3xl font-bold text-blue-400 mt-2">{statusCounts.in_progress}</p>
        </button>
        <button
          onClick={() => setFilter('resolved')}
          className={`card cursor-pointer transition-all text-left ${filter === 'resolved' ? 'ring-2 ring-green-500' : 'hover:bg-slate-700/50'}`}
        >
          <p className="text-sm font-medium text-slate-400">Resolved</p>
          <p className="text-3xl font-bold text-green-400 mt-2">{statusCounts.resolved}</p>
        </button>
      </div>

      {/* Ticket List */}
      <div className="card p-0 overflow-hidden">
        <div className="divide-y divide-slate-700">
          {filteredTickets.map((ticket) => (
            <div
              key={ticket.id}
              className={`p-6 hover:bg-slate-700/50 cursor-pointer transition-colors ${
                selectedTicket?.id === ticket.id ? 'bg-primary-500/10' : ''
              }`}
              onClick={() => setSelectedTicket(ticket)}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h3 className="text-lg font-medium text-white">{ticket.subject}</h3>
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getStatusColor(ticket.status)}`}>
                      {ticket.status.replace('_', ' ')}
                    </span>
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getPriorityColor(ticket.priority)}`}>
                      {ticket.priority}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 mt-2 text-sm text-slate-400">
                    <span>{ticket.email}</span>
                    <span>Created: {formatTime(ticket.createdAt)}</span>
                    <span>Updated: {formatTime(ticket.updatedAt)}</span>
                  </div>
                  <p className="mt-2 text-sm text-slate-300 line-clamp-2">
                    {ticket.messages[0]?.content}
                  </p>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <div className="flex -space-x-2">
                    {ticket.messages.some((m) => m.sender === 'ai') && (
                      <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center border-2 border-slate-800" title="AI handled">
                        <svg className="w-4 h-4 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                      </div>
                    )}
                    {ticket.messages.some((m) => m.sender === 'agent') && (
                      <div className="w-8 h-8 rounded-full bg-primary-500/20 flex items-center justify-center border-2 border-slate-800" title="Human agent responded">
                        <svg className="w-4 h-4 text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </div>
                    )}
                  </div>
                  <span className="text-sm text-slate-400">{ticket.messages.length} messages</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Ticket Detail Modal */}
      {selectedTicket && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col border border-slate-700">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700">
              <div>
                <h2 className="text-lg font-semibold text-white">{selectedTicket.subject}</h2>
                <div className="flex items-center gap-3 mt-1">
                  <span className="text-sm text-slate-400">{selectedTicket.email}</span>
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getStatusColor(selectedTicket.status)}`}>
                    {selectedTicket.status.replace('_', ' ')}
                  </span>
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getPriorityColor(selectedTicket.priority)}`}>
                    {selectedTicket.priority}
                  </span>
                </div>
              </div>
              <button
                onClick={() => setSelectedTicket(null)}
                className="text-slate-400 hover:text-white"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Conversation */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {selectedTicket.messages.map((message) => (
                <div
                  key={message.id}
                  className={`p-4 rounded-lg ${getSenderColor(message.sender)}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {message.sender === 'ai' && (
                        <svg className="w-5 h-5 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                      )}
                      {message.sender === 'agent' && (
                        <svg className="w-5 h-5 text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      )}
                      {message.sender === 'user' && (
                        <svg className="w-5 h-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      )}
                      <span className="font-medium text-white">{getSenderLabel(message.sender)}</span>
                    </div>
                    <span className="text-sm text-slate-400">{formatTime(message.timestamp)}</span>
                  </div>
                  <p className="text-slate-300 whitespace-pre-wrap">{message.content}</p>
                </div>
              ))}
            </div>

            {/* Reply Section */}
            <div className="border-t border-slate-700 p-6">
              {/* Attachment message */}
              {attachmentMessage && (
                <div className="mb-4 p-3 bg-green-500/10 border border-green-500/20 rounded-lg flex items-center justify-between">
                  <p className="text-sm text-green-400">{attachmentMessage}</p>
                  <button onClick={() => setAttachmentMessage(null)} className="text-green-400 hover:text-green-300">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              )}

              {/* Attached files display */}
              {attachedFiles.length > 0 && (
                <div className="mb-4 flex flex-wrap gap-2">
                  {attachedFiles.map((file, index) => (
                    <div key={index} className="flex items-center gap-2 px-3 py-1.5 bg-slate-700 rounded-lg">
                      <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <span className="text-sm text-slate-300">{file.name}</span>
                      <button
                        onClick={() => setAttachedFiles(files => files.filter((_, i) => i !== index))}
                        className="text-slate-400 hover:text-red-400"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex gap-4">
                <textarea
                  placeholder="Type your response..."
                  className="input flex-1 min-h-24 resize-none"
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                />
                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => {
                      if (selectedTicket && replyText) {
                        const content = attachedFiles.length > 0
                          ? `${replyText}\n\n[Attachments: ${attachedFiles.map(f => f.name).join(', ')}]`
                          : replyText;
                        replyToTicket.mutate({ ticketId: selectedTicket.id, content });
                        setAttachedFiles([]);
                      }
                    }}
                    disabled={!replyText || replyToTicket.isPending}
                    className="btn-primary whitespace-nowrap disabled:opacity-50"
                  >
                    {replyToTicket.isPending ? 'Sending...' : 'Send Reply'}
                  </button>
                  <select
                    className="input text-sm"
                    value={selectedTicket?.status ?? 'open'}
                    onChange={(e) => selectedTicket && updateStatus.mutate({ ticketId: selectedTicket.id, status: e.target.value as 'open' | 'in_progress' | 'resolved' | 'closed' })}
                  >
                    <option value="open">Keep Open</option>
                    <option value="in_progress">Mark In Progress</option>
                    <option value="resolved">Mark Resolved</option>
                  </select>
                </div>
              </div>
              <div className="flex items-center gap-4 mt-4">
                {/* Hidden file input */}
                <input
                  type="file"
                  ref={fileInputRef}
                  className="hidden"
                  multiple
                  onChange={(e) => {
                    if (e.target.files) {
                      const newFiles = Array.from(e.target.files);
                      setAttachedFiles(prev => [...prev, ...newFiles]);
                      setAttachmentMessage(`${newFiles.length} file(s) attached`);
                      setTimeout(() => setAttachmentMessage(null), 3000);
                    }
                    e.target.value = '';
                  }}
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="text-sm text-slate-400 hover:text-white flex items-center gap-1"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                  </svg>
                  Attach File {attachedFiles.length > 0 && `(${attachedFiles.length})`}
                </button>
                <div className="relative">
                  <button
                    onClick={() => setShowTemplates(!showTemplates)}
                    className="text-sm text-slate-400 hover:text-white flex items-center gap-1"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                    </svg>
                    Use Template
                  </button>
                  {/* Templates dropdown */}
                  {showTemplates && (
                    <div className="absolute bottom-full left-0 mb-2 w-64 bg-slate-800 rounded-lg shadow-xl border border-slate-700 overflow-hidden z-10">
                      <div className="p-2 border-b border-slate-700">
                        <p className="text-xs font-medium text-slate-400 uppercase">Response Templates</p>
                      </div>
                      <div className="max-h-64 overflow-y-auto">
                        {RESPONSE_TEMPLATES.map((template) => (
                          <button
                            key={template.id}
                            onClick={() => {
                              setReplyText(prev => prev ? `${prev}\n\n${template.content}` : template.content);
                              setShowTemplates(false);
                            }}
                            className="w-full px-3 py-2 text-left hover:bg-slate-700 transition-colors"
                          >
                            <p className="text-sm font-medium text-white">{template.name}</p>
                            <p className="text-xs text-slate-400 truncate">{template.content.substring(0, 50)}...</p>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => selectedTicket && escalateTicket.mutate(selectedTicket.id)}
                  disabled={escalateTicket.isPending}
                  className="text-sm text-slate-400 hover:text-white flex items-center gap-1 disabled:opacity-50"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  {escalateTicket.isPending ? 'Escalating...' : 'Escalate to Team'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
